// Client search request names (what the API returns in the search event) to the ProductSearch searchForm keys.
const FORM_KEYS = {
	searchTerm: "product",
	productClass: "productClass",
	casNumber: "casNumber",
	unNumber: "unNumber",
	vendor: "vendor",
	use: "productUse",
	inci: "inciName",
	kbMin: "kbValueMin",
	kbMax: "kbValueMax",
	anilineMin: "anilinePointMin",
	anilineMax: "anilinePointMax",
	anilineUnit: "anilinePointType",
	evapRateMin: "evaporationRateMin",
	evapRateMax: "evaporationRateMax",
	flashPointMin: "flashPointMin",
	flashPointMax: "flashPointMax",
	flashPointUnit: "flashPointType",
	boilingPointMinMin: "boilingPointMinMin",
	boilingPointMinMax: "boilingPointMinMax",
	boilingPointMinUnit: "boilingPointTypeMin",
	boilingPointMaxMin: "boilingPointMaxMin",
	boilingPointMaxMax: "boilingPointMaxMax",
	boilingPointMaxUnit: "boilingPointTypeMax",
	hlbValueMin: "hlbValueMin",
	hlbValueMax: "hlbValueMax",
	cloudPointMin: "cloudPointMin",
	cloudPointMax: "cloudPointMax",
	cloudPointUnit: "cloudPointType",
	relativeSolubilityMin: "relativeSolubilityMin",
	relativeSolubilityMax: "relativeSolubilityMax",
	requiredHlbMin: "requiredHlbMin",
	requiredHlbMax: "requiredHlbMax",
	foam: "foamCharacteristics",
	nonHap: "nonHap",
	vocExempt: "vocExempt",
	prop65Exempt: "prop65Exempt",
	saferChemical: "saferChemical",
	saferChoice: "saferChoice",
	carbCompliant: "carbCompliant",
	foodContact: "foodContact",
	foodPackaging: "foodPackaging",
	kosher: "kosher",
	nonGmo: "nonGmo",
	cosmos: "cosmos",
	massBalance: "massBalance",
	epaRegistered: "epaRegistered",
	reachApproved: "reachApproved",
	isWildcardSearch: "isWildcardSearch",
};

const TEXT_KEYS = [
	"product",
	"productClass",
	"casNumber",
	"unNumber",
	"vendor",
	"productUse",
	"inciName",
	"foamCharacteristics",
];
const UNIT_KEYS = [
	"anilinePointType",
	"flashPointType",
	"boilingPointTypeMin",
	"boilingPointTypeMax",
	"cloudPointType",
];

/** Search-event filters to a partial searchForm (unset keys get their blank defaults so the form fully resets). */
export function toSearchForm(filters) {
	const form = {};
	Object.entries(FORM_KEYS).forEach(([apiKey, formKey]) => {
		const value = filters ? filters[apiKey] : undefined;
		if (TEXT_KEYS.includes(formKey)) form[formKey] = value || "";
		else if (UNIT_KEYS.includes(formKey)) form[formKey] = value || "F";
		else if (formKey === "isWildcardSearch") form[formKey] = value !== false;
		else if (typeof value === "boolean") form[formKey] = value;
		else form[formKey] = value === undefined || value === null ? (isFlag(formKey) ? false : null) : value;
	});
	return form;
}

function isFlag(formKey) {
	return [
		"nonHap",
		"vocExempt",
		"prop65Exempt",
		"saferChemical",
		"saferChoice",
		"carbCompliant",
		"foodContact",
		"foodPackaging",
		"kosher",
		"nonGmo",
		"cosmos",
		"massBalance",
		"epaRegistered",
		"reachApproved",
	].includes(formKey);
}

/** The page's searchQuery (client names) trimmed of blanks, for the page context note. */
export function fromSearchQuery(query) {
	const out = {};
	Object.entries(query || {}).forEach(([key, value]) => {
		if (value === null || value === undefined || value === "" || value === false) return;
		out[key] = value;
	});
	return out;
}

// Plain labels for the chips on the SearchCard; the raw request names are tool-speak the user never typed.
const LABELS = {
	searchTerm: "Search",
	productClass: "Product class",
	casNumber: "CAS",
	unNumber: "UN number",
	vendor: "Vendor",
	use: "Product use",
	inci: "INCI name",
	foam: "Foam",
	nonHap: "Non HAP",
	vocExempt: "VOC exempt",
	prop65Exempt: "Prop 65 exempt",
	saferChemical: "Safer Chemical Ingredient List",
	saferChoice: "Safer Choice screened",
	carbCompliant: "CARB compliant",
	foodContact: "Food contact",
	foodPackaging: "Food packaging",
	kosher: "Kosher",
	nonGmo: "Non GMO",
	cosmos: "COSMOS",
	massBalance: "Mass balance",
	epaRegistered: "EPA registered",
	reachApproved: "REACH registered",
};

const RANGES = [
	{ min: "kbMin", max: "kbMax", label: "KB value" },
	{ min: "anilineMin", max: "anilineMax", unit: "anilineUnit", label: "Aniline point" },
	{ min: "evapRateMin", max: "evapRateMax", label: "Evaporation rate" },
	{ min: "flashPointMin", max: "flashPointMax", unit: "flashPointUnit", label: "Flash point" },
	{
		min: "boilingPointMinMin",
		max: "boilingPointMinMax",
		unit: "boilingPointMinUnit",
		label: "Boiling point min",
	},
	{
		min: "boilingPointMaxMin",
		max: "boilingPointMaxMax",
		unit: "boilingPointMaxUnit",
		label: "Boiling point max",
	},
	{ min: "hlbValueMin", max: "hlbValueMax", label: "HLB" },
	{ min: "cloudPointMin", max: "cloudPointMax", unit: "cloudPointUnit", label: "Cloud point" },
	{ min: "relativeSolubilityMin", max: "relativeSolubilityMax", label: "Relative solubility" },
	{ min: "requiredHlbMin", max: "requiredHlbMax", label: "Required HLB" },
];

const isSet = (value) => value !== null && value !== undefined && value !== "";

/** Human chips for the SearchCard: ranges fold their unit in, flags show their label only. */
export function describeFilters(filters) {
	const f = filters || {};
	const chips = [];
	const used = new Set(["isWildcardSearch"]);
	RANGES.forEach((r) => {
		used.add(r.min);
		used.add(r.max);
		if (r.unit) used.add(r.unit);
		const hasMin = isSet(f[r.min]);
		const hasMax = isSet(f[r.max]);
		if (!hasMin && !hasMax) return;
		const unit = r.unit ? ` \u00b0${f[r.unit] || "F"}` : "";
		let text;
		if (hasMin && hasMax) text = `${f[r.min]} to ${f[r.max]}${unit}`;
		else if (hasMin) text = `at least ${f[r.min]}${unit}`;
		else text = `up to ${f[r.max]}${unit}`;
		chips.push(`${r.label} ${text}`);
	});
	Object.entries(f).forEach(([key, value]) => {
		if (used.has(key) || !isSet(value) || value === false) return;
		const label = LABELS[key] || key;
		chips.push(value === true ? label : `${label}: ${value}`);
	});
	return chips;
}
